import { IfileDetails } from "src/pages/ManageStudies/models/interfaces";
import { generateFileName } from "src/pages/Studies/transformer";
import { apiService } from "../../../services";
import {
	fileTypes,
	IAddStudyData,
	ICompletionData,
	IDownloadUrlData,
	IDeleteFilePayload,
	IFileDownloadPayload,
	IFilesDetailsPayload,
	IPresignedUrlsResponse,
	IStandardApiResponse,
} from "src/pages/Studies/models/interfaces";
import { assertSafeHttpUrl } from "src/utils/security";

export const addNewStudy = async (values: IAddStudyData) => {
	return await apiService.post("entities/", values);
};

export const addNewStudyNotes = async (values: IAddStudyData) => {
	return await apiService.post("notes/entity", values);
};

export const addNewStudyQuant = async (values: IAddStudyData) => {
	return await apiService.post("quant/entity", values);
};

export const deleteStudy = async (study_id: string) => {
	return await apiService.delete("entities/", { study_id });
};

export const getNotesPreviewData = async (values) => {
	return await apiService.post("notes/request_preview", { ...values });
};

export const getNotesColumnMapping = async (values) => {
	return await apiService.get("notes/mapping/", { ...values });
};

export const saveNotesColumnMapping = async (values) => {
	return await apiService.put("notes/mapping", { ...values });
};

export const editStudy = async (values: IAddStudyData) => {
	return await apiService.put("entities/edit", values);
};

export const editStudyNotes = async (values: IAddStudyData) => {
	return await apiService.put("notes/entity_edit", values);
};

export const editStudyQuant = async (values: IAddStudyData) => {
	return await apiService.put("quant/entity_edit", values);
};

export const getFilesDetails = async (study_id: string, iteration_id?: string): Promise<IFilesDetailsPayload> => {
	const response = await apiService.get<IStandardApiResponse<IFilesDetailsPayload>>("files/", { study_id, iteration_id });
	return (
		response.data ?? {
			data: [],
			data_status: [],
			insights_status: {},
			quant_tabs: [],
		}
	);
};

export const getPresignedUrls = async (
	study_type_name: string,
	study_id: string,
	file_type: string,
	file_details: IfileDetails[],
	startDate?: string,
	endDate?: string,
): Promise<IPresignedUrlsResponse[]> => {
	const payload: any = { study_type_name, study_id, file_type, file_details };
	if (startDate) payload.start_date = startDate;
	if (endDate) payload.end_date = endDate;
	const response = await apiService.post<IStandardApiResponse<IPresignedUrlsResponse[]>>("files/initiate_upload", payload);
	return response.data ?? [];
};

export const getPresignedUrl = async (file_name: string, study_id: string, file_type): Promise<IPresignedUrlsResponse> => {
	return await apiService.post("files/get-presigned-url", { study_id, file_name, file_type });
};

export const completeMultipartUpload = async (completionData: ICompletionData) => {
	return await apiService.post("files/complete_upload", completionData);
};

export const downloadFile = async (values: IFileDownloadPayload) => {
	const { file_name, file_type } = values;
	const fileExtension = file_name.split(".").pop();

	const downloadFormatForReports = file_type === fileTypes.report || file_type === fileTypes.notes ? fileExtension : "";
	const ques_id = "";

	const fileName = generateFileName(file_type, file_name, ques_id, downloadFormatForReports);
	try {
		const response = await apiService.get<IStandardApiResponse<IDownloadUrlData>>("files/download", values);
		const downloadUrl = response.data?.url;
		if (!downloadUrl) {
			throw new Error("No download URL received");
		}
		const fileResponse = await fetch(assertSafeHttpUrl(downloadUrl));
		if (fileResponse.ok) {
			const blob = await fileResponse.blob(); // Convert the response to a blob
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = fileName; // Use the original file name for download
			document.body.appendChild(a); // Append the anchor to the body
			a.click(); // Trigger the download
			document.body.removeChild(a); // Remove the anchor from the body
			window.URL.revokeObjectURL(url); // Clean up the object URL
		} else {
			throw new Error("Failed to download file");
		}
	} catch (err) {
		console.error("Error downloading file: ", err);
		throw err;
	}
};

// Function to upload a single chunk to S3
const uploadChunk = async (presignedUrl, file, index, CHUNK_SIZE, totalChunks, onProgress) => {
	const start = index * CHUNK_SIZE;
	const end = Math.min(start + CHUNK_SIZE, file.size);
	const chunk = file.slice(start, end); // Get the chunk from the file

	const { url } = presignedUrl; // Presigned S3 URL

	// Step 1: Upload the chunk to S3 using the presigned URL with `fetch`
	const response = await fetch(assertSafeHttpUrl(url), {
		method: "PUT",
		body: chunk,
		headers: {
			// You can add content type or other necessary headers here
			// 'Content-Type': 'application/octet-stream', // Optional: Set to binary upload
		},
	});

	// Check if the upload was successful
	if (!response.ok) {
		throw new Error(`Error uploading chunk ${index + 1}: ${response.statusText}`);
	}

	// Step 2: Calculate progress manually since fetch doesn't support `onUploadProgress`
	const percentCompleted = ((index + 1) / totalChunks) * 100;
	onProgress?.({ percent: percentCompleted });

	// Step 3: Extract ETag from the response headers
	const etag = response.headers.get("etag");

	// Return the ETag and PartNumber for this chunk
	return {
		PartNumber: index + 1,
		ETag: etag,
	};
};

// Function to handle the chunked upload process
export const uploadFileInChunksToS3 = async (presigned_urls_info, file, CHUNK_SIZE) => {
	const totalChunks = Math.ceil(file.size / CHUNK_SIZE); // If the file is small enough for a single upload, handle it as a single chunk

	const etags = await Promise.all(
		Array.from({ length: totalChunks }).map((_, index) => uploadChunk(presigned_urls_info[index], file, index, CHUNK_SIZE, totalChunks, file.onProgress)),
	);

	return etags;
};

export const uploadFileToS3 = async (url, file) => {
	// Step 1: Create a new AbortController to handle upload cancellation if needed
	const controller = new AbortController();
	const { signal } = controller;

	// Step 2: Upload the file to S3 using fetch
	const uploadResponse = await fetch(assertSafeHttpUrl(url), {
		method: "PUT",
		body: file,
		headers: {
			"Content-Type": file.type,
		},
		signal, // Attach the AbortController's signal to handle cancellation
	});

	// Step 3: Check if the upload was successful
	if (!uploadResponse.ok) {
		throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
	}

	// Step 4: Return the response from the fetch call
	return uploadResponse;
};

export const getPresignedUrlsQuant = async (
	study_type_name: string,
	study_id: string,
	file_type: string,
	file_details: IfileDetails[],
	startDate?: string,
	endDate?: string,
	iteration_id?: string,
	id_type?: string,
): Promise<IPresignedUrlsResponse[]> => {
	const payload: any = { study_type_name, study_id, file_type, file_details, iteration_id };
	if (startDate) payload.start_date = startDate;
	if (endDate) payload.end_date = endDate;
	if (id_type) payload.id_type = id_type;
	const response = await apiService.post<IStandardApiResponse<IPresignedUrlsResponse[]>>("quant/initiate_upload", payload);
	return response.data ?? [];
};

export const completeMultipartUploadQuant = async (completionData: ICompletionData) => {
	return await apiService.post("quant/complete_upload", completionData);
};

export const addNewIteration = async (values) => {
	return await apiService.post("quant/iteration", values);
};

export const updateIteration = async (values) => {
	return await apiService.put("quant/edit-iteration", values);
};

export const downloadFileQuant = async (values: IFileDownloadPayload) => {
	const { file_name, file_type } = values;
	const lastDotIndex = file_name.lastIndexOf(".");
	const fileExtension = lastDotIndex > 0 ? file_name.substring(lastDotIndex + 1) : "";
	const ques_id = "";

	try {
		const response = await apiService.get<IStandardApiResponse<IDownloadUrlData>>("quant/download", values);
		const downloadUrl = response.data?.url;
		if (!downloadUrl) {
			throw new Error("No download URL received");
		}
		const fileName = generateFileName(file_type, file_name, ques_id, response.data?.file_type || response.data?.extension || fileExtension);
		const fileResponse = await fetch(assertSafeHttpUrl(downloadUrl));
		if (fileResponse.ok) {
			const blob = await fileResponse.blob(); // Convert the response to a blob
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = fileName; // Use the original file name for download
			document.body.appendChild(a); // Append the anchor to the body
			a.click(); // Trigger the download
			document.body.removeChild(a); // Remove the anchor from the body
			window.URL.revokeObjectURL(url); // Clean up the object URL
		} else {
			throw new Error("Failed to download file");
		}
	} catch (err) {
		console.error("Error downloading file: ", err);
		throw err;
	}
};

export const downloadQuantLogs = async (values, isDataPage = false) => {
	const { file_name } = values;
	const fileName = generateFileName(fileTypes.errorlogs, file_name, "", isDataPage ? "csv" : "xlsx");
	try {
		const response = await apiService.get<IStandardApiResponse<IDownloadUrlData>>("quant/download_logs", values);
		const downloadUrl = response.data?.url;
		if (!downloadUrl) {
			throw new Error("No download URL received");
		}
		const fileResponse = await fetch(assertSafeHttpUrl(downloadUrl));
		if (fileResponse.ok) {
			const blob = await fileResponse.blob(); // Convert the response to a blob
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = fileName; // Use the original file name for download
			document.body.appendChild(a); // Append the anchor to the body
			a.click(); // Trigger the download
			document.body.removeChild(a); // Remove the anchor from the body
			window.URL.revokeObjectURL(url); // Clean up the object URL
		} else {
			throw new Error("Failed to download file");
		}
	} catch (err) {
		console.error("Error downloading file: ", err);
		throw err;
	}
};

export const downloadQuantLogsBlob = async (values) => {
	const { file_name } = values;
	const fileName = generateFileName(fileTypes.rawData, file_name, "", "csv");
	try {
		const response = await apiService.downloadFile("quant/download_logs", values);
		if (!response) {
			throw new Error("No data received");
		}
		const url = window.URL.createObjectURL(response);
		const a = document.createElement("a");
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	} catch (err) {
		console.error("Error downloading file: ", err);
		throw err;
	}
};

export const addStudyContext = async (study_id: string, context: string) => {
	return await apiService.put("entities/context", { study_id, context });
};

export const addItertionContext = async (study_id: string, iteration_id: string, iteration_context: string) => {
	return await apiService.put("quant/iteration-context", { study_id, iteration_id, iteration_context });
};

export const retryIngestion = async (values) => {
	return await apiService.post("quant/retry_ingestion", values);
};

export const saveNotesTemplate = async (study_id: string, template_id: string) => {
	return await apiService.post("notes/notes-config/template", { study_id, template_id });
};

export const generateQuantInsights = async (study_id: string) => {
	return await apiService.post("quant/generate/insights", { study_id });
};

export const generateHelixADM = async (study_id: string, iteration_id: string) => {
	return await apiService.post("quant/auto_generate_adm", {
		study_id,
		iteration_id,
	});
};

export const getRespondantData = async (study_id: string, isQuant = false) => {
	const URL = isQuant ? "/quant/display" : "/v1/primary-segment/display";
	return await apiService.get(URL, { study_id });
};

export const getNPIStatus = async (study_id: string) => {
	return await apiService.get("/v1/primary-segment/status", { study_id });
};

export const parseMapping = async (study_id: string, file_id: string) => {
	return await apiService.post("/files/mapping/parse", { study_id, file_id });
};

export const deleteMapping = async (study_id: string, isQuant = false) => {
	const URL = isQuant ? "/quant/mapping" : "/files/mapping";
	return await apiService.delete(URL, { study_id });
};

export const writeToMongo = async (study_id: string) => {
	return await apiService.post("/v1/primary-segment/write_to_mongo", { study_id });
};

export const getMappingErrorLogs = async (isQuant = false, study_id: string) => {
	const URL = isQuant ? "/quant/download_error_log" : "v1/primary-segment/download_error_log";
	return await apiService.downloadTemplateFile(URL, { study_id });
};

export const lambdaTrigger = async (profile_id: string, study_id?: string) => {
	return await apiService.post("/v1/custom-segments/match", { profile_id, study_id });
};

export const segmentMatchStatus = async (profile_id: string) => {
	return await apiService.get("/v1/custom-segments/match/status", { profile_id });
};
